import { useState, useEffect } from 'react';
import { 
    Plus, 
    Search, 
    MoreHorizontal,
    Truck,
    Building,
    Edit,
    Trash2,
    Shield,
    AlertTriangle,
    CheckCircle,
    XCircle,
    ChevronDown,
    ChevronUp,
    Calendar,
    FileText,
    TrendingUp
} from 'lucide-react';
import { Card, CardContent, CardHeader, CardTitle } from '../components/ui/card';
import { Button } from '../components/ui/button';
import { Input } from '../components/ui/input';
import { Badge } from '../components/ui/badge';
import { Label } from '../components/ui/label';
import { Progress } from '../components/ui/progress';
import {
    Select,
    SelectContent,
    SelectItem,
    SelectTrigger,
    SelectValue,
} from '../components/ui/select';
import {
    Dialog,
    DialogContent,
    DialogDescription,
    DialogFooter,
    DialogHeader,
    DialogTitle,
} from '../components/ui/dialog';
import {
    DropdownMenu,
    DropdownMenuContent,
    DropdownMenuItem,
    DropdownMenuTrigger,
} from '../components/ui/dropdown-menu';
import {
    Tabs,
    TabsContent,
    TabsList,
    TabsTrigger,
} from '../components/ui/tabs';
import {
    Accordion,
    AccordionContent,
    AccordionItem,
    AccordionTrigger,
} from '../components/ui/accordion';
import { toast } from 'sonner';

const API_URL = process.env.REACT_APP_BACKEND_URL;

const US_STATES = [
    'AL', 'AK', 'AZ', 'AR', 'CA', 'CO', 'CT', 'DE', 'FL', 'GA',
    'HI', 'ID', 'IL', 'IN', 'IA', 'KS', 'KY', 'LA', 'ME', 'MD',
    'MA', 'MI', 'MN', 'MS', 'MO', 'MT', 'NE', 'NV', 'NH', 'NJ',
    'NM', 'NY', 'NC', 'ND', 'OH', 'OK', 'OR', 'PA', 'RI', 'SC',
    'SD', 'TN', 'TX', 'UT', 'VT', 'VA', 'WA', 'WV', 'WI', 'WY'
];

const CSA_CATEGORIES = [
    { field: 'csa_unsafe_driving', label: 'Unsafe Driving', weight: 1.5 },
    { field: 'csa_hos_compliance', label: 'HOS Compliance', weight: 1.4 },
    { field: 'csa_vehicle_maintenance', label: 'Vehicle Maintenance', weight: 1.3 },
    { field: 'csa_driver_fitness', label: 'Driver Fitness', weight: 1.0 },
    { field: 'csa_controlled_substances', label: 'Controlled Substances', weight: 1.2 },
    { field: 'csa_crash_indicator', label: 'Crash Indicator', weight: 1.6 },
    { field: 'csa_hazmat', label: 'Hazmat', weight: 1.1 }
];

const getRiskBadgeStyle = (level) => {
    switch (level) {
        case 'low':
            return 'bg-green-500/20 text-green-400 border-green-500/30';
        case 'moderate':
            return 'bg-yellow-500/20 text-yellow-400 border-yellow-500/30';
        case 'high':
            return 'bg-orange-500/20 text-orange-400 border-orange-500/30';
        case 'critical':
            return 'bg-red-500/20 text-red-400 border-red-500/30';
        default:
            return 'bg-zinc-500/20 text-zinc-400 border-zinc-500/30';
    }
};

const getComplianceBadgeStyle = (status) => {
    switch (status) {
        case 'approved':
            return 'bg-green-500/20 text-green-400 border-green-500/30';
        case 'review':
            return 'bg-yellow-500/20 text-yellow-400 border-yellow-500/30';
        case 'blocked':
            return 'bg-red-500/20 text-red-400 border-red-500/30';
        default:
            return 'bg-zinc-500/20 text-zinc-400 border-zinc-500/30';
    }
};

const getCSAColor = (value) => {
    if (value === null || value === undefined) return 'bg-zinc-600';
    if (value >= 80) return 'bg-red-500';
    if (value >= 65) return 'bg-orange-500';
    if (value >= 40) return 'bg-yellow-500';
    return 'bg-green-500';
};

const isInsuranceExpiringSoon = (dateStr) => {
    if (!dateStr) return false;
    const expDate = new Date(dateStr);
    const today = new Date();
    const daysUntil = Math.floor((expDate - today) / (1000 * 60 * 60 * 24));
    return daysUntil <= 30 && daysUntil >= 0;
};

const isInsuranceExpired = (dateStr) => {
    if (!dateStr) return false;
    return new Date(dateStr) < new Date();
};

export default function CarriersPage() {
    const [carriers, setCarriers] = useState([]);
    const [trucks, setTrucks] = useState([]);
    const [loading, setLoading] = useState(true);
    const [searchQuery, setSearchQuery] = useState('');
    const [showCarrierDialog, setShowCarrierDialog] = useState(false);
    const [showRiskDialog, setShowRiskDialog] = useState(false);
    const [selectedCarrier, setSelectedCarrier] = useState(null);
    const [riskAnalysis, setRiskAnalysis] = useState(null);
    const [creating, setCreating] = useState(false);
    const [editMode, setEditMode] = useState(false);

    const [carrierForm, setCarrierForm] = useState({
        company_name: '',
        mc_number: '',
        dot_number: '',
        contact_name: '',
        phone: '',
        email: '',
        city: '',
        state: '',
        insurance_expiration_date: '',
        factoring_company: '',
        compliance_status: 'review',
        csa_unsafe_driving: '',
        csa_hos_compliance: '',
        csa_driver_fitness: '',
        csa_vehicle_maintenance: '',
        csa_controlled_substances: '',
        csa_crash_indicator: '',
        csa_hazmat: '',
        notes: ''
    });

    useEffect(() => {
        fetchData();
    }, []);

    const fetchData = async () => {
        setLoading(true);
        try {
            const [carriersRes, trucksRes] = await Promise.all([
                fetch(`${API_URL}/api/carriers`, { credentials: 'include' }),
                fetch(`${API_URL}/api/trucks`, { credentials: 'include' })
            ]);

            if (carriersRes.ok) {
                const carriersData = await carriersRes.json();
                setCarriers(carriersData);
            }
            if (trucksRes.ok) {
                const trucksData = await trucksRes.json();
                setTrucks(trucksData);
            }
        } catch (error) {
            console.error('Failed to fetch data:', error);
            toast.error('Failed to fetch carriers');
        } finally {
            setLoading(false);
        }
    };

    const resetForm = () => {
        setCarrierForm({
            company_name: '',
            mc_number: '',
            dot_number: '',
            contact_name: '',
            phone: '',
            email: '',
            city: '',
            state: '',
            insurance_expiration_date: '',
            factoring_company: '',
            compliance_status: 'review',
            csa_unsafe_driving: '',
            csa_hos_compliance: '',
            csa_driver_fitness: '',
            csa_vehicle_maintenance: '',
            csa_controlled_substances: '',
            csa_crash_indicator: '',
            csa_hazmat: '',
            notes: ''
        });
        setEditMode(false);
        setSelectedCarrier(null);
    };

    const handleCreateCarrier = async () => {
        if (!carrierForm.company_name || !carrierForm.mc_number || !carrierForm.dot_number) {
            toast.error('Company name, MC#, and DOT# are required');
            return;
        }

        setCreating(true);
        try {
            // Convert CSA fields to numbers or null
            const payload = {
                ...carrierForm,
                csa_unsafe_driving: carrierForm.csa_unsafe_driving !== '' ? parseFloat(carrierForm.csa_unsafe_driving) : null,
                csa_hos_compliance: carrierForm.csa_hos_compliance !== '' ? parseFloat(carrierForm.csa_hos_compliance) : null,
                csa_driver_fitness: carrierForm.csa_driver_fitness !== '' ? parseFloat(carrierForm.csa_driver_fitness) : null,
                csa_vehicle_maintenance: carrierForm.csa_vehicle_maintenance !== '' ? parseFloat(carrierForm.csa_vehicle_maintenance) : null,
                csa_controlled_substances: carrierForm.csa_controlled_substances !== '' ? parseFloat(carrierForm.csa_controlled_substances) : null,
                csa_crash_indicator: carrierForm.csa_crash_indicator !== '' ? parseFloat(carrierForm.csa_crash_indicator) : null,
                csa_hazmat: carrierForm.csa_hazmat !== '' ? parseFloat(carrierForm.csa_hazmat) : null,
            };

            const url = editMode 
                ? `${API_URL}/api/carriers/${selectedCarrier.carrier_id}`
                : `${API_URL}/api/carriers`;
            
            const response = await fetch(url, {
                method: editMode ? 'PATCH' : 'POST',
                headers: { 'Content-Type': 'application/json' },
                credentials: 'include',
                body: JSON.stringify(payload)
            });

            if (response.ok) {
                toast.success(editMode ? 'Carrier updated successfully!' : 'Carrier created successfully!');
                setShowCarrierDialog(false);
                resetForm();
                fetchData();
            } else {
                const error = await response.json();
                toast.error(error.detail || 'Failed to save carrier');
            }
        } catch (error) {
            console.error('Failed to save carrier:', error);
            toast.error('Failed to save carrier');
        } finally {
            setCreating(false);
        }
    };

    const handleEditCarrier = (carrier) => {
        setEditMode(true);
        setSelectedCarrier(carrier);
        setCarrierForm({
            company_name: carrier.company_name || '',
            mc_number: carrier.mc_number || '',
            dot_number: carrier.dot_number || '',
            contact_name: carrier.contact_name || '',
            phone: carrier.phone || '',
            email: carrier.email || '',
            city: carrier.city || '',
            state: carrier.state || '',
            insurance_expiration_date: carrier.insurance_expiration_date || '',
            factoring_company: carrier.factoring_company || '',
            compliance_status: carrier.compliance_status || 'review',
            csa_unsafe_driving: carrier.csa_unsafe_driving ?? '',
            csa_hos_compliance: carrier.csa_hos_compliance ?? '',
            csa_driver_fitness: carrier.csa_driver_fitness ?? '',
            csa_vehicle_maintenance: carrier.csa_vehicle_maintenance ?? '',
            csa_controlled_substances: carrier.csa_controlled_substances ?? '',
            csa_crash_indicator: carrier.csa_crash_indicator ?? '',
            csa_hazmat: carrier.csa_hazmat ?? '',
            notes: carrier.notes || ''
        });
        setShowCarrierDialog(true);
    };

    const handleDeleteCarrier = async (carrierId) => {
        if (!window.confirm('Are you sure you want to delete this carrier? Any linked trucks will be unlinked.')) return;

        try {
            const response = await fetch(`${API_URL}/api/carriers/${carrierId}`, {
                method: 'DELETE',
                credentials: 'include'
            });

            if (response.ok) {
                toast.success('Carrier deleted successfully');
                fetchData();
            } else {
                toast.error('Failed to delete carrier');
            }
        } catch (error) {
            console.error('Failed to delete carrier:', error);
            toast.error('Failed to delete carrier');
        }
    };

    const handleViewRiskAnalysis = async (carrier) => {
        try {
            const response = await fetch(`${API_URL}/api/carriers/${carrier.carrier_id}/risk-analysis`, {
                credentials: 'include'
            });
            if (response.ok) {
                const data = await response.json();
                setRiskAnalysis(data);
                setSelectedCarrier(carrier);
                setShowRiskDialog(true);
            } else {
                toast.error('Failed to fetch risk analysis');
            }
        } catch (error) {
            console.error('Failed to fetch risk analysis:', error);
            toast.error('Failed to fetch risk analysis');
        }
    };

    const filteredCarriers = carriers.filter(carrier => {
        if (!searchQuery) return true;
        const query = searchQuery.toLowerCase();
        return (
            carrier.company_name?.toLowerCase().includes(query) ||
            carrier.mc_number?.toLowerCase().includes(query) ||
            carrier.dot_number?.toLowerCase().includes(query) ||
            carrier.contact_name?.toLowerCase().includes(query)
        );
    });

    const getLinkedTruckCount = (carrierId) => {
        return trucks.filter(t => t.carrier_id === carrierId).length;
    };

    return (
        <div className="space-y-6 animate-fade-in" data-testid="carriers-page">
            {/* Page Header */}
            <div className="flex items-center justify-between">
                <div>
                    <h1 className="text-2xl font-bold text-white tracking-tight uppercase">Carrier Management</h1>
                    <p className="text-zinc-500 text-sm mt-1">Manage carriers with CSA safety scores and risk analysis</p>
                </div>
                <Button 
                    onClick={() => { resetForm(); setShowCarrierDialog(true); }}
                    className="bg-orange-500 hover:bg-orange-600 text-white"
                    data-testid="add-carrier-btn"
                >
                    <Plus className="w-4 h-4 mr-2" />
                    Add Carrier
                </Button>
            </div>

            {/* Summary Cards */}
            <div className="grid grid-cols-4 gap-4">
                <Card className="bg-zinc-900/50 border-zinc-800">
                    <CardContent className="p-4">
                        <div className="flex items-center justify-between">
                            <div>
                                <p className="text-zinc-500 text-xs uppercase">Total Carriers</p>
                                <p className="text-2xl font-bold text-white">{carriers.length}</p>
                            </div>
                            <Building className="w-8 h-8 text-orange-500" />
                        </div>
                    </CardContent>
                </Card>
                <Card className="bg-zinc-900/50 border-zinc-800">
                    <CardContent className="p-4">
                        <div className="flex items-center justify-between">
                            <div>
                                <p className="text-zinc-500 text-xs uppercase">Approved</p>
                                <p className="text-2xl font-bold text-green-400">
                                    {carriers.filter(c => c.compliance_status === 'approved').length}
                                </p>
                            </div>
                            <CheckCircle className="w-8 h-8 text-green-500" />
                        </div>
                    </CardContent>
                </Card>
                <Card className="bg-zinc-900/50 border-zinc-800">
                    <CardContent className="p-4">
                        <div className="flex items-center justify-between">
                            <div>
                                <p className="text-zinc-500 text-xs uppercase">High/Critical Risk</p>
                                <p className="text-2xl font-bold text-red-400">
                                    {carriers.filter(c => c.risk_level === 'high' || c.risk_level === 'critical').length}
                                </p>
                            </div>
                            <AlertTriangle className="w-8 h-8 text-red-500" />
                        </div>
                    </CardContent>
                </Card>
                <Card className="bg-zinc-900/50 border-zinc-800">
                    <CardContent className="p-4">
                        <div className="flex items-center justify-between">
                            <div>
                                <p className="text-zinc-500 text-xs uppercase">Insurance Expiring</p>
                                <p className="text-2xl font-bold text-yellow-400">
                                    {carriers.filter(c => isInsuranceExpiringSoon(c.insurance_expiration_date)).length}
                                </p>
                            </div>
                            <Calendar className="w-8 h-8 text-yellow-500" />
                        </div>
                    </CardContent>
                </Card>
            </div>

            {/* Search */}
            <Card className="bg-zinc-900/50 border-zinc-800">
                <CardContent className="p-4">
                    <div className="relative">
                        <Search className="w-4 h-4 text-zinc-500 absolute left-3 top-1/2 -translate-y-1/2" />
                        <Input
                            placeholder="Search carriers by name, MC#, or DOT#..."
                            value={searchQuery}
                            onChange={(e) => setSearchQuery(e.target.value)}
                            className="pl-9 bg-zinc-900 border-zinc-800"
                            data-testid="search-input"
                        />
                    </div>
                </CardContent>
            </Card>

            {/* Carriers Table */}
            <Card className="bg-zinc-900/50 border-zinc-800">
                <CardContent className="p-0">
                    {loading ? (
                        <div className="flex items-center justify-center py-12">
                            <div className="w-8 h-8 border-2 border-orange-500 border-t-transparent rounded-full animate-spin" />
                        </div>
                    ) : filteredCarriers.length === 0 ? (
                        <div className="empty-state py-12 text-center">
                            <Building className="w-12 h-12 text-zinc-700 mx-auto mb-4" />
                            <p className="text-zinc-500">No carriers found</p>
                            <Button 
                                variant="outline" 
                                className="mt-4 border-zinc-700"
                                onClick={() => setShowCarrierDialog(true)}
                            >
                                Add Your First Carrier
                            </Button>
                        </div>
                    ) : (
                        <div className="overflow-x-auto">
                            <table className="data-table w-full">
                                <thead>
                                    <tr className="border-b border-zinc-800">
                                        <th className="text-left p-4 text-zinc-400 text-xs uppercase">Company</th>
                                        <th className="text-left p-4 text-zinc-400 text-xs uppercase">MC / DOT</th>
                                        <th className="text-left p-4 text-zinc-400 text-xs uppercase">Contact</th>
                                        <th className="text-left p-4 text-zinc-400 text-xs uppercase">Compliance</th>
                                        <th className="text-left p-4 text-zinc-400 text-xs uppercase">Risk</th>
                                        <th className="text-left p-4 text-zinc-400 text-xs uppercase">Insurance</th>
                                        <th className="text-left p-4 text-zinc-400 text-xs uppercase">Trucks</th>
                                        <th className="text-left p-4 text-zinc-400 text-xs uppercase"></th>
                                    </tr>
                                </thead>
                                <tbody>
                                    {filteredCarriers.map((carrier) => (
                                        <tr 
                                            key={carrier.carrier_id} 
                                            className="border-b border-zinc-800/50 hover:bg-zinc-800/30"
                                            data-testid={`carrier-row-${carrier.carrier_id}`}
                                        >
                                            <td className="p-4">
                                                <div className="text-white font-semibold">{carrier.company_name}</div>
                                                <div className="text-zinc-500 text-xs">{carrier.carrier_id}</div>
                                            </td>
                                            <td className="p-4">
                                                <div className="text-orange-400 font-mono text-sm">{carrier.mc_number}</div>
                                                <div className="text-zinc-500 text-xs">{carrier.dot_number}</div>
                                            </td>
                                            <td className="p-4">
                                                <div className="text-white text-sm">{carrier.contact_name || '-'}</div>
                                                <div className="text-zinc-500 text-xs">{carrier.phone || ''}</div>
                                            </td>
                                            <td className="p-4">
                                                <Badge className={`${getComplianceBadgeStyle(carrier.compliance_status)} text-xs uppercase`}>
                                                    {carrier.compliance_status}
                                                </Badge>
                                            </td>
                                            <td className="p-4">
                                                <div className="flex items-center gap-2">
                                                    <Badge className={`${getRiskBadgeStyle(carrier.risk_level)} text-xs uppercase`}>
                                                        {carrier.risk_level || 'unknown'}
                                                    </Badge>
                                                    {carrier.risk_score !== null && (
                                                        <span className="text-zinc-500 text-xs">
                                                            ({carrier.risk_score?.toFixed(0)})
                                                        </span>
                                                    )}
                                                </div>
                                            </td>
                                            <td className="p-4">
                                                {carrier.insurance_expiration_date ? (
                                                    <div className={`text-sm ${
                                                        isInsuranceExpired(carrier.insurance_expiration_date) 
                                                            ? 'text-red-400' 
                                                            : isInsuranceExpiringSoon(carrier.insurance_expiration_date)
                                                                ? 'text-yellow-400'
                                                                : 'text-zinc-400'
                                                    }`}>
                                                        {carrier.insurance_expiration_date}
                                                        {isInsuranceExpired(carrier.insurance_expiration_date) && (
                                                            <div className="text-xs text-red-500">EXPIRED</div>
                                                        )}
                                                        {isInsuranceExpiringSoon(carrier.insurance_expiration_date) && !isInsuranceExpired(carrier.insurance_expiration_date) && (
                                                            <div className="text-xs text-yellow-500">Expiring Soon</div>
                                                        )}
                                                    </div>
                                                ) : (
                                                    <span className="text-zinc-600">-</span>
                                                )}
                                            </td>
                                            <td className="p-4">
                                                <div className="flex items-center gap-1">
                                                    <Truck className="w-4 h-4 text-zinc-500" />
                                                    <span className="text-white">{getLinkedTruckCount(carrier.carrier_id)}</span>
                                                </div>
                                            </td>
                                            <td className="p-4">
                                                <DropdownMenu>
                                                    <DropdownMenuTrigger asChild>
                                                        <Button variant="ghost" size="icon" className="h-8 w-8">
                                                            <MoreHorizontal className="h-4 w-4" />
                                                        </Button>
                                                    </DropdownMenuTrigger>
                                                    <DropdownMenuContent align="end" className="bg-zinc-900 border-zinc-800">
                                                        <DropdownMenuItem 
                                                            className="cursor-pointer"
                                                            onClick={() => handleViewRiskAnalysis(carrier)}
                                                        >
                                                            <TrendingUp className="w-4 h-4 mr-2" />
                                                            Risk Analysis
                                                        </DropdownMenuItem>
                                                        <DropdownMenuItem 
                                                            className="cursor-pointer"
                                                            onClick={() => handleEditCarrier(carrier)}
                                                        >
                                                            <Edit className="w-4 h-4 mr-2" />
                                                            Edit
                                                        </DropdownMenuItem>
                                                        <DropdownMenuItem 
                                                            className="cursor-pointer text-red-400"
                                                            onClick={() => handleDeleteCarrier(carrier.carrier_id)}
                                                        >
                                                            <Trash2 className="w-4 h-4 mr-2" />
                                                            Delete
                                                        </DropdownMenuItem>
                                                    </DropdownMenuContent>
                                                </DropdownMenu>
                                            </td>
                                        </tr>
                                    ))}
                                </tbody>
                            </table>
                        </div>
                    )}
                </CardContent>
            </Card>

            {/* Add/Edit Carrier Dialog */}
            <Dialog open={showCarrierDialog} onOpenChange={(open) => { setShowCarrierDialog(open); if (!open) resetForm(); }}>
                <DialogContent className="bg-zinc-900 border-zinc-800 max-w-2xl max-h-[90vh] overflow-y-auto">
                    <DialogHeader>
                        <DialogTitle className="text-white">{editMode ? 'Edit Carrier' : 'Add New Carrier'}</DialogTitle>
                        <DialogDescription className="text-zinc-400">
                            Enter carrier details including CSA safety percentiles
                        </DialogDescription>
                    </DialogHeader>
                    
                    <div className="space-y-6 py-4">
                        {/* Basic Info Section */}
                        <div className="space-y-4">
                            <h3 className="text-sm font-semibold text-zinc-300 uppercase tracking-wider flex items-center gap-2">
                                <Building className="w-4 h-4" />
                                Basic Information
                            </h3>
                            <div>
                                <Label className="text-zinc-400">Company Name *</Label>
                                <Input
                                    value={carrierForm.company_name}
                                    onChange={(e) => setCarrierForm({ ...carrierForm, company_name: e.target.value })}
                                    className="bg-zinc-800 border-zinc-700 mt-1"
                                    placeholder="ABC Trucking LLC"
                                    data-testid="carrier-name-input"
                                />
                            </div>
                            <div className="grid grid-cols-2 gap-4">
                                <div>
                                    <Label className="text-zinc-400">MC Number *</Label>
                                    <Input
                                        value={carrierForm.mc_number}
                                        onChange={(e) => setCarrierForm({ ...carrierForm, mc_number: e.target.value })}
                                        className="bg-zinc-800 border-zinc-700 mt-1"
                                        placeholder="MC-123456"
                                        data-testid="carrier-mc-input"
                                    />
                                </div>
                                <div>
                                    <Label className="text-zinc-400">DOT Number *</Label>
                                    <Input
                                        value={carrierForm.dot_number}
                                        onChange={(e) => setCarrierForm({ ...carrierForm, dot_number: e.target.value })}
                                        className="bg-zinc-800 border-zinc-700 mt-1"
                                        placeholder="DOT-789012"
                                        data-testid="carrier-dot-input"
                                    />
                                </div>
                            </div>
                            <div className="grid grid-cols-2 gap-4">
                                <div>
                                    <Label className="text-zinc-400">Contact Name</Label>
                                    <Input
                                        value={carrierForm.contact_name}
                                        onChange={(e) => setCarrierForm({ ...carrierForm, contact_name: e.target.value })}
                                        className="bg-zinc-800 border-zinc-700 mt-1"
                                        placeholder="John Smith"
                                    />
                                </div>
                                <div>
                                    <Label className="text-zinc-400">Phone</Label>
                                    <Input
                                        value={carrierForm.phone}
                                        onChange={(e) => setCarrierForm({ ...carrierForm, phone: e.target.value })}
                                        className="bg-zinc-800 border-zinc-700 mt-1"
                                        placeholder="(555) 123-4567"
                                    />
                                </div>
                            </div>
                            <div className="grid grid-cols-2 gap-4">
                                <div>
                                    <Label className="text-zinc-400">City</Label>
                                    <Input
                                        value={carrierForm.city}
                                        onChange={(e) => setCarrierForm({ ...carrierForm, city: e.target.value })}
                                        className="bg-zinc-800 border-zinc-700 mt-1"
                                        placeholder="Dallas"
                                    />
                                </div>
                                <div>
                                    <Label className="text-zinc-400">State</Label>
                                    <Select
                                        value={carrierForm.state}
                                        onValueChange={(value) => setCarrierForm({ ...carrierForm, state: value })}
                                    >
                                        <SelectTrigger className="bg-zinc-800 border-zinc-700 mt-1">
                                            <SelectValue placeholder="Select state" />
                                        </SelectTrigger>
                                        <SelectContent className="bg-zinc-900 border-zinc-800 max-h-60">
                                            {US_STATES.map(state => (
                                                <SelectItem key={state} value={state}>{state}</SelectItem>
                                            ))}
                                        </SelectContent>
                                    </Select>
                                </div>
                            </div>
                        </div>

                        {/* Insurance & Factoring Section */}
                        <div className="space-y-4 border-t border-zinc-800 pt-4">
                            <h3 className="text-sm font-semibold text-zinc-300 uppercase tracking-wider flex items-center gap-2">
                                <Shield className="w-4 h-4" />
                                Insurance & Factoring
                            </h3>
                            <div className="grid grid-cols-2 gap-4">
                                <div>
                                    <Label className="text-zinc-400">Insurance Expiration</Label>
                                    <Input
                                        type="date"
                                        value={carrierForm.insurance_expiration_date}
                                        onChange={(e) => setCarrierForm({ ...carrierForm, insurance_expiration_date: e.target.value })}
                                        className="bg-zinc-800 border-zinc-700 mt-1"
                                    />
                                </div>
                                <div>
                                    <Label className="text-zinc-400">Factoring Company</Label>
                                    <Input
                                        value={carrierForm.factoring_company}
                                        onChange={(e) => setCarrierForm({ ...carrierForm, factoring_company: e.target.value })}
                                        className="bg-zinc-800 border-zinc-700 mt-1"
                                        placeholder="Optional"
                                    />
                                </div>
                            </div>
                            <div>
                                <Label className="text-zinc-400">Compliance Status</Label>
                                <Select
                                    value={carrierForm.compliance_status}
                                    onValueChange={(value) => setCarrierForm({ ...carrierForm, compliance_status: value })}
                                >
                                    <SelectTrigger className="bg-zinc-800 border-zinc-700 mt-1">
                                        <SelectValue placeholder="Select status" />
                                    </SelectTrigger>
                                    <SelectContent className="bg-zinc-900 border-zinc-800">
                                        <SelectItem value="approved">Approved</SelectItem>
                                        <SelectItem value="review">Under Review</SelectItem>
                                        <SelectItem value="blocked">Blocked</SelectItem>
                                    </SelectContent>
                                </Select>
                            </div>
                        </div>

                        {/* CSA Percentiles Section */}
                        <div className="space-y-4 border-t border-zinc-800 pt-4">
                            <h3 className="text-sm font-semibold text-zinc-300 uppercase tracking-wider flex items-center gap-2">
                                <AlertTriangle className="w-4 h-4" />
                                CSA BASIC Percentiles (0-100)
                            </h3>
                            <p className="text-xs text-zinc-500">
                                Higher percentiles indicate worse safety performance. Scores above 80 may trigger FMCSA intervention.
                            </p>
                            <div className="grid grid-cols-2 gap-4">
                                {CSA_CATEGORIES.map(({ field, label, weight }) => (
                                    <div key={field}>
                                        <Label className="text-zinc-400 text-xs">
                                            {label} <span className="text-zinc-600">(×{weight})</span>
                                        </Label>
                                        <Input
                                            type="number"
                                            min="0"
                                            max="100"
                                            value={carrierForm[field]}
                                            onChange={(e) => setCarrierForm({ ...carrierForm, [field]: e.target.value })}
                                            className="bg-zinc-800 border-zinc-700 mt-1"
                                            placeholder="0-100"
                                        />
                                    </div>
                                ))}
                            </div>
                        </div>

                        {/* Notes */}
                        <div className="border-t border-zinc-800 pt-4">
                            <Label className="text-zinc-400">Notes</Label>
                            <textarea
                                value={carrierForm.notes}
                                onChange={(e) => setCarrierForm({ ...carrierForm, notes: e.target.value })}
                                className="w-full bg-zinc-800 border border-zinc-700 rounded-md p-2 mt-1 text-white min-h-[80px]"
                                placeholder="Additional notes..."
                            />
                        </div>
                    </div>

                    <DialogFooter>
                        <Button 
                            variant="outline" 
                            onClick={() => { setShowCarrierDialog(false); resetForm(); }}
                            className="border-zinc-700"
                        >
                            Cancel
                        </Button>
                        <Button 
                            onClick={handleCreateCarrier}
                            disabled={creating}
                            className="bg-orange-500 hover:bg-orange-600 text-white"
                            data-testid="submit-carrier-btn"
                        >
                            {creating ? 'Saving...' : (editMode ? 'Update Carrier' : 'Add Carrier')}
                        </Button>
                    </DialogFooter>
                </DialogContent>
            </Dialog>

            {/* Risk Analysis Dialog */}
            <Dialog open={showRiskDialog} onOpenChange={setShowRiskDialog}>
                <DialogContent className="bg-zinc-900 border-zinc-800 max-w-lg max-h-[90vh] overflow-y-auto">
                    <DialogHeader>
                        <DialogTitle className="text-white flex items-center gap-2">
                            <TrendingUp className="w-5 h-5 text-orange-500" />
                            Risk Analysis
                        </DialogTitle>
                        <DialogDescription className="text-zinc-400">
                            {selectedCarrier?.company_name} ({selectedCarrier?.mc_number})
                        </DialogDescription>
                    </DialogHeader>
                    
                    {riskAnalysis && (
                        <div className="space-y-6 py-4">
                            {/* Overall Risk Score */}
                            <div className="bg-zinc-800/50 rounded-lg p-4 border border-zinc-700">
                                <div className="flex items-center justify-between mb-3">
                                    <span className="text-zinc-400 text-sm">Overall Risk Score</span>
                                    <Badge className={`${getRiskBadgeStyle(riskAnalysis.risk_level)} text-sm uppercase`}>
                                        {riskAnalysis.risk_level}
                                    </Badge>
                                </div>
                                <div className="text-4xl font-bold text-white mb-2">
                                    {riskAnalysis.risk_score !== null ? riskAnalysis.risk_score.toFixed(1) : 'N/A'}
                                    <span className="text-lg text-zinc-500">/100</span>
                                </div>
                                <Progress 
                                    value={riskAnalysis.risk_score || 0} 
                                    className="h-2"
                                />
                                {riskAnalysis.intervention_required && (
                                    <div className="mt-3 p-2 bg-red-500/10 border border-red-500/30 rounded text-red-400 text-xs flex items-center gap-2">
                                        <AlertTriangle className="w-4 h-4" />
                                        FMCSA intervention threshold reached
                                    </div>
                                )}
                            </div>

                            {/* Booking Eligibility */}
                            <div className={`rounded-lg p-4 border ${
                                riskAnalysis.booking_eligible?.eligible 
                                    ? 'bg-green-500/10 border-green-500/30' 
                                    : 'bg-red-500/10 border-red-500/30'
                            }`}>
                                <div className="flex items-center gap-2 mb-2">
                                    {riskAnalysis.booking_eligible?.eligible 
                                        ? <CheckCircle className="w-5 h-5 text-green-400" />
                                        : <XCircle className="w-5 h-5 text-red-400" />
                                    }
                                    <span className={`font-semibold ${
                                        riskAnalysis.booking_eligible?.eligible ? 'text-green-400' : 'text-red-400'
                                    }`}>
                                        {riskAnalysis.booking_eligible?.eligible ? 'Eligible for Dispatch' : 'Dispatch Blocked'}
                                    </span>
                                </div>
                                {riskAnalysis.booking_eligible?.blocking_reasons?.map((reason, i) => (
                                    <div key={i} className="text-red-400 text-sm pl-7">• {reason}</div>
                                ))}
                                {riskAnalysis.booking_eligible?.warnings?.map((warning, i) => (
                                    <div key={i} className="text-yellow-400 text-sm pl-7">• {warning}</div>
                                ))}
                            </div>

                            {/* CSA Breakdown */}
                            <div className="space-y-3">
                                <h4 className="text-sm font-semibold text-zinc-300 uppercase">CSA BASIC Breakdown</h4>
                                {riskAnalysis.csa_breakdown?.map((cat) => (
                                    <div key={cat.field} className="flex items-center gap-3">
                                        <div className="w-32 text-zinc-400 text-xs truncate">{cat.category}</div>
                                        <div className="flex-1">
                                            <Progress 
                                                value={cat.percentile || 0} 
                                                className={`h-2 ${getCSAColor(cat.percentile)}`}
                                            />
                                        </div>
                                        <div className="w-12 text-right">
                                            <span className={`text-sm font-mono ${
                                                cat.percentile >= 80 ? 'text-red-400' :
                                                cat.percentile >= 65 ? 'text-orange-400' :
                                                cat.percentile >= 40 ? 'text-yellow-400' :
                                                cat.percentile !== null ? 'text-green-400' : 'text-zinc-500'
                                            }`}>
                                                {cat.percentile !== null ? cat.percentile : '-'}
                                            </span>
                                        </div>
                                    </div>
                                ))}
                            </div>

                            {/* Insurance Status */}
                            <div className="bg-zinc-800/50 rounded-lg p-4 border border-zinc-700">
                                <h4 className="text-sm font-semibold text-zinc-300 uppercase mb-2">Insurance Status</h4>
                                <div className={`text-sm ${
                                    riskAnalysis.insurance_status?.expired ? 'text-red-400' :
                                    riskAnalysis.insurance_status?.status === 'expiring_soon' ? 'text-yellow-400' :
                                    riskAnalysis.insurance_status?.status === 'valid' ? 'text-green-400' : 'text-zinc-400'
                                }`}>
                                    {riskAnalysis.insurance_status?.status?.toUpperCase().replace('_', ' ')}
                                    {riskAnalysis.insurance_status?.days_until_expiry !== null && (
                                        <span className="text-zinc-500 ml-2">
                                            ({riskAnalysis.insurance_status.days_until_expiry} days)
                                        </span>
                                    )}
                                </div>
                            </div>

                            {/* Warnings */}
                            {riskAnalysis.warnings?.length > 0 && (
                                <div className="space-y-2">
                                    <h4 className="text-sm font-semibold text-zinc-300 uppercase">Warnings</h4>
                                    {riskAnalysis.warnings.map((w, i) => (
                                        <div 
                                            key={i}
                                            className={`text-sm p-2 rounded ${
                                                w.type === 'critical' ? 'bg-red-500/10 text-red-400' :
                                                w.type === 'alert' ? 'bg-orange-500/10 text-orange-400' :
                                                'bg-yellow-500/10 text-yellow-400'
                                            }`}
                                        >
                                            {w.message}
                                        </div>
                                    ))}
                                </div>
                            )}
                        </div>
                    )}

                    <DialogFooter>
                        <Button 
                            variant="outline" 
                            onClick={() => setShowRiskDialog(false)}
                            className="border-zinc-700"
                        >
                            Close
                        </Button>
                    </DialogFooter>
                </DialogContent>
            </Dialog>
        </div>
    );
}
